/**
 * Typed API client for ClearIntake.
 * Wraps fetch with authentication headers and the ApiResponse<T> envelope.
 */

import { ApiResponse, AIFlag } from '@clearintake/shared';

// JWT is stored in memory only — never in localStorage (HIPAA requirement)
let authToken: string | null = null;

/** Build a GHL contact detail deep-link */
export function ghlContactUrl(locationId: string, contactGhlId: string): string {
  const base = import.meta.env['VITE_GHL_APP_URL'] ?? 'https://account.seeuwell.com';
  return `${base}/v2/location/${locationId}/contacts/detail/${contactGhlId}`;
}

/** Deep link to the location's Contacts smart list (where staff can add a new contact). */
export function ghlContactsUrl(locationId: string): string {
  const base = import.meta.env['VITE_GHL_APP_URL'] ?? 'https://account.seeuwell.com';
  return `${base}/v2/location/${locationId}/contacts/smart_list/All`;
}

export function setAuthToken(token: string): void {
  authToken = token;
}

export function clearAuthToken(): void {
  authToken = null;
}

export function hasAuthToken(): boolean {
  return authToken !== null;
}

export function getAuthToken(): string | null {
  return authToken;
}

// ---------------------------------------------------------------------------
// API base URL
//
// Production builds (Cloudflare Pages) set VITE_API_BASE_URL to the Azure
// Functions origin; Vite inlines it at build time so every call targets the
// API cross-origin. Local dev leaves it unset, so requests stay relative
// ('/api/...') and the Vite dev proxy forwards them to the Functions server.
// ---------------------------------------------------------------------------

export const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '';

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const res = await fetch(`${API_BASE}/api/${path}`, { ...options, headers });

  const text = await res.text();
  if (!text) {
    return { success: false, error: `Server returned empty response (HTTP ${res.status} ${res.statusText})` } as ApiResponse<T>;
  }
  let body: ApiResponse<T>;
  try {
    body = JSON.parse(text) as ApiResponse<T>;
  } catch {
    // Non-JSON response — surface the raw text (truncated) so we can diagnose
    return { success: false, error: `Non-JSON response (HTTP ${res.status}): ${text.slice(0, 300)}` } as ApiResponse<T>;
  }
  return body;
}

async function apiFetchForm<T>(
  path: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  // Do NOT set Content-Type — browser sets multipart boundary automatically
  const res = await fetch(`${API_BASE}/api/${path}`, { method: 'POST', headers, body: formData });
  return res.json() as Promise<ApiResponse<T>>;
}

// ---------------------------------------------------------------------------
// Shared types (mirror backend response shapes)
// ---------------------------------------------------------------------------

/** Which channel(s) a notification actually went out on (mirrors api lib/notify). */
export type NotifiedVia = 'email' | 'sms' | 'email_sms' | 'none';

export interface MessageSummary {
  id: string;
  subject: string;
  fromType: string;
  fromId: string;
  toContactGhlId: string;
  toContactName: string;
  readAt: string | null;
  /** Staff-side unread: the patient has the latest activity and staff hasn't read
   *  it since. Drives the inbox unread dot. */
  staffUnread: boolean;
  createdAt: string;
  /** GHL-style status flags (staff-only actions). Null = not starred/trashed. */
  starredAt: string | null;
  deletedAt: string | null;
  hasReplies: boolean;
  hasAttachments: boolean;
}

export interface ReplyDetail {
  id: string;
  fromType: string;
  fromId: string;
  body: string;
  createdAt: string;
  attachments: AttachmentMeta[];
}

export interface AttachmentMeta {
  id: string;
  filename: string;
  sizeBytes: number;
  uploadedAt: string;
}

// Client-side attachment limits. The server (messages/attach) is authoritative
// (size, MIME allowlist, magic-byte sniff); these just fail fast in the UI.
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024; // 25 MB
export const ATTACHMENT_MAX_COUNT = 5;                // files per reply
export const ATTACHMENT_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,application/pdf,image/jpeg,image/png,image/heic,image/heif,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ATTACHMENT_EXTS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif', '.docx'];

/** Returns an error string if the file is not an allowed attachment, else null. */
export function validateAttachment(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!ATTACHMENT_EXTS.includes(ext)) {
    return `"${file.name}" is an unsupported type. Allowed: PDF, JPG, PNG, HEIC, DOCX.`;
  }
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > ATTACHMENT_MAX_BYTES) return `"${file.name}" exceeds the 25 MB limit.`;
  return null;
}

export interface MessageDetail {
  id: string;
  subject: string;
  body: string;
  fromType: string;
  fromId: string;
  fromName: string;
  toContactGhlId: string;
  toContactName: string;
  readAt: string | null;
  createdAt: string;
  replies: ReplyDetail[];
  attachments: AttachmentMeta[];
}

export interface GHLContactResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/** AI contact-match candidate returned by ai.matchContact. */
export interface MatchCandidate {
  contactId:  string;
  name:       string;
  email:      string;
  phone:      string;
  confidence: number;
  method:     string;
}

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------

export const auth = {
  ghlSSO: (ssoToken: string) =>
    apiFetch<{ token: string; expiresIn: number }>('auth/ghl-sso', {
      method: 'POST',
      body: JSON.stringify({ ssoToken }),
    }),

  // NOTE: the legacy `locationSSO` client method was removed. The backend no
  // longer mints a staff session from a bare locationId (it is not a secret);
  // staff auth now requires the encrypted GHL SSO payload via `ghlSSO`.

  requestOTP: (token: string) =>
    apiFetch<{ message: string; contactMasked?: string }>('auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  verifyOTP: (token: string, otp: string) =>
    apiFetch<{ token: string; entityType: string; entityId: string }>('auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ token, otp }),
    }),
};

// ---------------------------------------------------------------------------
// Messages endpoints
// ---------------------------------------------------------------------------

export const messages = {
  list: (params?: { page?: number; limit?: number; contactId?: string; unreadOnly?: boolean; filter?: 'starred' | 'trash' }) => {
    const qs = new URLSearchParams();
    if (params?.page)        qs.set('page',       String(params.page));
    if (params?.limit)       qs.set('limit',      String(params.limit));
    if (params?.contactId)   qs.set('contactId',  params.contactId);
    if (params?.unreadOnly)  qs.set('unreadOnly', 'true');
    if (params?.filter)      qs.set('filter',     params.filter);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<{ messages: MessageSummary[]; total: number; unreadCount: number }>(`messages${query}`);
  },

  read: (messageId: string) =>
    apiFetch<{ message: MessageDetail }>(`messages/${messageId}`),

  send: (payload: { toContactGhlId: string; subject: string; body: string }) =>
    apiFetch<{ messageId: string; magicLink: string; notified: boolean; notifiedVia?: NotifiedVia }>('messages', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  reply: (messageId: string, body: string) =>
    apiFetch<{ replyId: string }>(`messages/${messageId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  /** Toggle the staff-side read/unread state of a thread (the inbox dot). */
  setReadStatus: (messageId: string, read: boolean) =>
    apiFetch<{ read: boolean }>(`messages/${messageId}/read-status`, {
      method: 'POST',
      body: JSON.stringify({ read }),
    }),

  /** Staff-only GHL-style status actions on a thread: star and/or trash (soft delete). */
  setStatus: (messageId: string, patch: { starred?: boolean; deleted?: boolean }) =>
    apiFetch<{ starred: boolean | null; deleted: boolean | null }>(`messages/${messageId}/status`, {
      method: 'POST',
      body: JSON.stringify(patch),
    }),

  /** Cheap thread-change probe (no PHI, no audit) — used by live-update polling. */
  meta: (messageId: string) =>
    apiFetch<{ replyCount: number; lastActivityAt: string }>(`messages/${messageId}/meta`),

  attach: (messageId: string, file: File, replyId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = replyId ? `?replyId=${encodeURIComponent(replyId)}` : '';
    return apiFetchForm<{ attachmentId: string; filename: string; sizeBytes: number }>(
      `messages/${messageId}/attachments${qs}`,
      fd,
    );
  },

  downloadUrl: (messageId: string, attachmentId: string) =>
    apiFetch<{ url: string }>(`messages/${messageId}/attachments/${attachmentId}`),
};

// ---------------------------------------------------------------------------
// GHL proxy endpoints
// ---------------------------------------------------------------------------

export const ghl = {
  searchContacts: (q: string) =>
    apiFetch<{ contacts: GHLContactResult[] }>(`contacts?q=${encodeURIComponent(q)}`),
};

// ---------------------------------------------------------------------------
// Forms types
// ---------------------------------------------------------------------------

/** Ordered patient-identity field config on a form/package (mirrors shared
 *  IdentityFieldConfig). NULL config server-side = the DEFAULT trio; the web
 *  treats an absent `identityFields` (old API without SI) as DEFAULT too. */
export interface IdentityFieldDef {
  key:      'firstName' | 'lastName' | 'dob' | 'email' | 'phone';
  required: boolean;
}

/** Identity as entered/collected client-side (no server-stamped source).
 *  dob is ISO 'YYYY-MM-DD'. Sent on submit / package verify-otp. */
export interface SubmitterInput {
  firstName?: string;
  lastName?:  string;
  dob?:       string;
  email?:     string;
  phone?:     string;
}

/** Owner identity shown in the staff submissions list/detail (display subset +
 *  server-stamped source). */
export interface SubmitterView {
  firstName?: string;
  lastName?:  string;
  dob?:       string;
  source:     'contact' | 'patient';
}

export interface FormSchema {
  id:          string;
  locationId:  string;
  name:        string;
  description: string;
  aiMode:      string;
  authMode:    string;
  isPublic:    boolean;
  publicSlug:  string | null;
  fieldCount:  number;
  createdBy:   string;
  createdAt:   string;
  updatedAt:   string;
  /** Patient-identity config (server sends the DEFAULT trio for a NULL row).
   *  Inherited by FormSchemaDetail + the public schema response. */
  identityFields?: IdentityFieldDef[];
}

export interface FormSchemaDetail extends FormSchema {
  schemaJson: {
    components?: Array<{
      key?: string;
      label?: string;
      type?: string;
      input?: boolean;
      validate?: { required?: boolean; [key: string]: unknown };
      components?: unknown[];
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  } | null;
}

export interface SubmissionSummary {
  id:                string;
  formId:            string;
  formName:          string;
  contactGhlId:      string | null;
  preview:           { firstName?: string; lastName?: string; email?: string; dob?: string };
  /** First-class encrypted owner identity (null for pre-feature rows → the
   *  `preview` heuristic is the fallback). Inherited by SubmissionDetail. */
  submitter?:        SubmitterView | null;
  /** Number of attachments on this submission. Set by the list endpoint; the
   *  detail view fetches the full attachment list separately. */
  attachmentCount?:  number;
  aiFlags:           unknown[];
  matchConfidence:   number | null;
  matchReviewStatus: string | null;
  hasAiSummary:      boolean;
  /** Staff review state (keys off reviewed_at). */
  reviewed:          boolean;
  reviewedAt:        string | null;
  /** GHL-style status flags (staff-only actions). Null = not starred/trashed. */
  starredAt:         string | null;
  deletedAt:         string | null;
  submittedAt:       string;
}

export interface SubmissionDetail extends SubmissionSummary {
  data:        Record<string, unknown>;
  transcript:  unknown[] | null;
  aiSummary:   string | null;
  schemaJson:  unknown;
  ipAddress:   string;
}

export interface SubmissionAttachmentMeta {
  id:          string;
  filename:    string;
  contentType: string | null;
  sizeBytes:   number;
  uploadedAt:  string;
}

// ---------------------------------------------------------------------------
// Forms endpoints
// ---------------------------------------------------------------------------

export interface FormAssignment {
  id:           string;
  formId:       string;
  formName:     string;
  contactGhlId: string;
  status:       'pending' | 'completed' | 'expired';
  completedAt:  string | null;
  expiresAt:    string;
  createdAt:    string;
}

export interface ContactAssignmentDetail {
  assignmentId: string;
  formId:       string;
  formName:     string;
  description:  string;
  schemaJson:   unknown;
  aiMode:       string;
  authMode:     string;
  locationId:   string;
  expiresAt:    string;
}

export const forms = {
  schemas: {
    list: () => apiFetch<FormSchema[]>('forms/schemas'),
    get: (id: string) => apiFetch<FormSchemaDetail>(`forms/schemas/${id}`),
    getPublic: (locationId: string, slug: string) =>
      apiFetch<FormSchemaDetail>(`forms/schemas/public/${locationId}/${slug}`),
    create: (payload: {
      name: string; description?: string | undefined; schemaJson: unknown;
      aiMode?: string | undefined; authMode?: string | undefined; isPublic?: boolean | undefined; publicSlug?: string | undefined;
      // null = back to DEFAULT trio, [] = no identity step, array = explicit config.
      identityFields?: IdentityFieldDef[] | null | undefined;
    }) => apiFetch<{ id: string; publicSlug: string | null }>('forms/schemas', {
      method: 'POST', body: JSON.stringify(payload),
    }),
    update: (id: string, payload: {
      name?: string | undefined; description?: string | undefined; schemaJson?: unknown;
      aiMode?: string | undefined; authMode?: string | undefined; isPublic?: boolean | undefined; publicSlug?: string | undefined;
      identityFields?: IdentityFieldDef[] | null | undefined;
    }) => apiFetch<{ id: string }>(`forms/schemas/${id}`, {
      method: 'PUT', body: JSON.stringify(payload),
    }),
    delete: (id: string) => apiFetch<{ id: string }>(`forms/schemas/${id}`, { method: 'DELETE' }),
  },

  assignments: {
    list: (params?: { contactId?: string; status?: 'pending' | 'completed'; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.contactId) qs.set('contactId', params.contactId);
      if (params?.status)    qs.set('status',    params.status);
      if (params?.limit)     qs.set('limit',      String(params.limit));
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<{ assignments: FormAssignment[]; total: number }>(`forms/assignments${q}`);
    },
    getForContact: (assignmentId: string) =>
      apiFetch<ContactAssignmentDetail>(`forms/assignments/${assignmentId}`),
  },

  assign: (payload: { formId: string; contactGhlId: string; expiresInHours?: number }) =>
    apiFetch<{ assignmentId: string; magicLink: string; notified: boolean; notifiedVia?: NotifiedVia }>('forms/assign', {
      method: 'POST', body: JSON.stringify(payload),
    }),

  submit: (payload: {
    formId: string; assignmentId?: string | undefined; locationId?: string | undefined;
    data: Record<string, unknown>; transcript?: unknown[] | undefined;
    grant?: string | undefined;
    // Package steps: identify the run item + carry the run grant (public runs).
    packageRunItemId?: string | undefined; runGrant?: string | undefined;
    // Patient-entered owner identity (public single-form / open flows). Server
    // stamps source; assigned/package submits derive identity server-side.
    submitter?: SubmitterInput | undefined;
  }) => apiFetch<{ message: string }>('forms/submit', {
    method: 'POST', body: JSON.stringify(payload),
  }),

  submissions: {
    list: (params?: {
      formId?: string | undefined; contactId?: string | undefined; status?: 'new' | 'reviewed' | undefined;
      filter?: 'starred' | 'trash' | undefined;
      page?: number | undefined; limit?: number | undefined;
    }) => {
      const qs = new URLSearchParams();
      if (params?.formId)    qs.set('formId',    params.formId);
      if (params?.contactId) qs.set('contactId', params.contactId);
      if (params?.status)    qs.set('status',    params.status);
      if (params?.filter)    qs.set('filter',    params.filter);
      if (params?.page)      qs.set('page',      String(params.page));
      if (params?.limit)     qs.set('limit',     String(params.limit));
      const q = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<{ submissions: SubmissionSummary[]; total: number; unreadCount: number }>(`forms/submissions${q}`);
    },
    get: (id: string) => apiFetch<SubmissionDetail>(`forms/submissions/${id}`),

    /** List a submission's file attachments (staff review panel). */
    getAttachments: (id: string) =>
      apiFetch<{ attachments: SubmissionAttachmentMeta[] }>(`forms/submissions/${id}/attachments`),

    /** Resolve a short-lived SAS download URL for one attachment (authenticated, no token in DOM). */
    getFileUrl: (attachmentId: string) =>
      apiFetch<{ url: string }>(`forms/files/${attachmentId}?mode=json`),

    /** Staff-only GHL-style status actions on a submission: reviewed, star, trash (soft delete). */
    setStatus: (id: string, patch: { reviewed?: boolean; starred?: boolean; deleted?: boolean }) =>
      apiFetch<{ reviewed: boolean | null; starred: boolean | null; deleted: boolean | null }>(`forms/submissions/${id}/status`, {
        method: 'POST',
        body: JSON.stringify(patch),
      }),
  },
};

// ---------------------------------------------------------------------------
// Public form OTP endpoints (token-less — for cold public form visitors)
//
// Distinct from `auth.requestOTP/verifyOTP`, which require a pre-existing
// magic-link token bound to a known GHL contact. A public visitor has neither,
// so these mint an ephemeral, form-scoped OTP and return a short-lived signed
// `grant` (NOT a contact JWT) that is forwarded on submit.
// Backend contract: POST /api/forms/public/request-otp + /api/forms/public/verify-otp
// (see blockers — backend endpoints must land for this to function).
// ---------------------------------------------------------------------------

export const publicForm = {
  requestOtp: (payload: { locationId: string; formId: string; channel: 'email' | 'phone'; contact: string }) =>
    apiFetch<{ sent: boolean; masked: string }>('forms/public/request-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  verifyOtp: (payload: { locationId: string; formId: string; channel: 'email' | 'phone'; contact: string; otp: string }) =>
    apiFetch<{ grant: string; expiresIn: number }>('forms/public/verify-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ---------------------------------------------------------------------------
// Forms Packages types
//
// A "package" bundles multiple forms into ONE patient link. A run is one
// person's pass through a package; each step still produces its own submission.
// Public runs authenticate with a short-lived `x-run-grant` header (role-less
// grant), assigned runs with the normal contact JWT.
// ---------------------------------------------------------------------------

export interface PackageSummary {
  id:          string;
  name:        string;
  description: string | null;
  isPublic:    boolean;
  publicSlug:  string | null;
  authMode:    string;
  itemCount:   number;
  formNames:   string[];
  createdBy:   string | null;
  createdAt:   string;
  updatedAt:   string;
  /** Package identity config (wins over member forms during a run). */
  identityFields?: IdentityFieldDef[];
}

export interface PackageItemMeta {
  formId:    string;
  formName:  string | null;
  sortOrder: number;
}

export interface PackageDetail {
  id:          string;
  name:        string;
  description: string | null;
  isPublic:    boolean;
  publicSlug:  string | null;
  authMode:    string;
  createdBy:   string | null;
  createdAt:   string;
  updatedAt:   string;
  items:       PackageItemMeta[];
  /** Package identity config (wins over member forms during a run). */
  identityFields?: IdentityFieldDef[];
}

export interface PackageRunSummary {
  id:             string;
  packageId:      string;
  packageName:    string;
  contactGhlId:   string;
  origin:         string;
  status:         'pending' | 'completed' | 'expired';
  itemsTotal:     number;
  itemsCompleted: number;
  completedAt:    string | null;
  expiresAt:      string;
  createdAt:      string;
}

/** One step's progress within a run (from the run detail endpoint). */
export interface RunItemMeta {
  itemId:      string;
  formId:      string;
  formName:    string;
  sortOrder:   number;
  completedAt: string | null;
}

export interface PackageRunDetail {
  runId:       string;
  packageName: string;
  description: string | null;
  origin:      string;
  expiresAt:   string;
  completedAt: string | null;
  items:       RunItemMeta[];
  nextItemId:  string | null;
}

/** A single package step to render — mirrors ContactAssignmentDetail (+ itemId). */
export interface RunItemDetail {
  itemId:      string;
  formId:      string;
  formName:    string;
  description: string;
  schemaJson:  unknown;
  aiMode:      string;
  authMode:    string;
  locationId:  string;
  expiresAt:   string;
}

export interface PublicPackageMeta {
  id:          string;
  name:        string;
  description: string | null;
  authMode:    string;
  forms:       { name: string; sortOrder: number }[];
  /** Fields the public identity screen must collect (server sends the DEFAULT
   *  trio for a NULL-config package). */
  identityFields?: IdentityFieldDef[];
}

// ---------------------------------------------------------------------------
// Forms Packages endpoints
// ---------------------------------------------------------------------------

/** Staff CRUD + assign. */
export const packages = {
  list: () => apiFetch<PackageSummary[]>('forms/packages'),
  get: (id: string) => apiFetch<PackageDetail>(`forms/packages/${id}`),
  create: (payload: {
    name: string; description?: string | undefined; formIds: string[];
    isPublic?: boolean | undefined; publicSlug?: string | undefined; authMode?: string | undefined;
    // null = DEFAULT trio, [] = no identity step, array = explicit config.
    identityFields?: IdentityFieldDef[] | null | undefined;
  }) => apiFetch<{ id: string; publicSlug: string | null }>('forms/packages', {
    method: 'POST', body: JSON.stringify(payload),
  }),
  update: (id: string, payload: {
    name?: string | undefined; description?: string | undefined; formIds?: string[] | undefined;
    isPublic?: boolean | undefined; publicSlug?: string | undefined; authMode?: string | undefined;
    // Packages PUT is a full-row replace — the builder always sends this.
    identityFields?: IdentityFieldDef[] | null | undefined;
  }) => apiFetch<{ id: string; publicSlug: string | null }>(`forms/packages/${id}`, {
    method: 'PUT', body: JSON.stringify(payload),
  }),
  remove: (id: string) => apiFetch<{ id: string }>(`forms/packages/${id}`, { method: 'DELETE' }),
  assign: (payload: { packageId: string; contactGhlId: string; expiresInHours?: number }) =>
    apiFetch<{ runId: string; magicLink: string; notified: boolean; notifiedVia?: NotifiedVia }>('forms/packages/assign', {
      method: 'POST', body: JSON.stringify(payload),
    }),
};

/** Run read endpoints. `grant`, when passed, is forwarded as the x-run-grant
 *  header (public runs); assigned runs rely on the in-memory contact JWT. */
export const packageRuns = {
  get: (id: string, grant?: string) =>
    apiFetch<PackageRunDetail>(`forms/package-runs/${id}`, grant ? { headers: { 'x-run-grant': grant } } : {}),
  getItem: (runId: string, itemId: string, grant?: string) =>
    apiFetch<RunItemDetail>(`forms/package-runs/${runId}/items/${itemId}`, grant ? { headers: { 'x-run-grant': grant } } : {}),
  listForStaff: (params?: { contactId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.contactId) qs.set('contactId', params.contactId);
    if (params?.limit)     qs.set('limit',     String(params.limit));
    const q = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<{ runs: PackageRunSummary[]; total: number }>(`forms/package-runs${q}`);
  },
};

/** Public package endpoints (token-less — cold public /pkg visitors). */
export const publicPackage = {
  getMeta: (locationId: string, slug: string) =>
    apiFetch<PublicPackageMeta>(`forms/packages/public/${locationId}/${slug}`),
  requestOtp: (payload: { locationId: string; packageId?: string; slug?: string; contact: string }) =>
    apiFetch<{ sent: boolean; masked: string }>('forms/packages/public/request-otp', {
      method: 'POST', body: JSON.stringify(payload),
    }),
  verifyOtp: (payload: {
    locationId: string; packageId?: string; slug?: string; contact: string; otp: string;
    // Patient identity captured on the /pkg identity screen (server stamps the
    // run's submitter_enc; omit when the package's config is empty).
    submitter?: SubmitterInput | undefined;
  }) =>
    apiFetch<{ grant: string; runId: string; expiresIn: number }>('forms/packages/public/verify-otp', {
      method: 'POST', body: JSON.stringify(payload),
    }),
};

// ---------------------------------------------------------------------------
// AI endpoints
// ---------------------------------------------------------------------------

export const ai = {
  buildForm: (description: string) =>
    apiFetch<{ schema: { components?: unknown[]; [k: string]: unknown } }>('ai/build-form', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),

  summarize: (submissionId: string, force = false) =>
    apiFetch<{ summary: string; flags: AIFlag[]; cached: boolean }>('ai/summarize', {
      method: 'POST',
      body: JSON.stringify({ submissionId, force }),
    }),

  matchContact: (submissionId: string) =>
    apiFetch<{
      candidates: MatchCandidate[];
      status?: string;
      autoMatched?: boolean;
      contactInfo?: { name?: string; email?: string; phone?: string };
      alreadyMatched?: boolean;
      contactGhlId?: string;
    }>('ai/match-contact', {
      method: 'POST',
      body: JSON.stringify({ submissionId }),
    }),

  // Resolve an AI-suggested contact match. Backend supports confirm/reject/create_new;
  // the UI 'Reassign' control maps onto a 'confirm' with a manually chosen contactGhlId.
  confirmMatch: (payload: {
    submissionId: string;
    action: 'confirm' | 'reject' | 'create_new';
    contactGhlId?: string;
  }) =>
    apiFetch<{ action: string; contactGhlId: string | null }>('ai/confirm-match', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ---------------------------------------------------------------------------
// PDF endpoints
// ---------------------------------------------------------------------------

export const pdf = {
  generate: (params: { type: 'form_submission' | 'message_thread'; entityId: string; force?: boolean }) =>
    apiFetch<{ url: string; expiresAt: string; cached: boolean; filename: string }>(
      'pdf/generate',
      { method: 'POST', body: JSON.stringify(params) },
    ),
};

// ---------------------------------------------------------------------------
// Notifications endpoints
// ---------------------------------------------------------------------------

export const notifications = {
  counts: () =>
    apiFetch<{ messageUnread: number; formSubmissionNew: number }>('notifications/counts'),
};

// ---------------------------------------------------------------------------
// Settings types + endpoints
// ---------------------------------------------------------------------------

export interface TenantSettings {
  practiceName:         string;
  ghlWorkflowId:        string;
  defaultAiMode:        'ai_only' | 'toggle' | 'form_only';
  defaultMagicLinkExpiry: number; // hours
  jsreportUrlOverride:  string;
  messageReceivedWebhookUrl: string; // GHL Inbound Webhook trigger URL
  singleUseLinks:       boolean;     // links consumed after first verification
  aiChatEnabled:        boolean;     // master switch for patient AI intake chat
}

export const settings = {
  get: () => apiFetch<TenantSettings>('settings'),
  update: (payload: Partial<TenantSettings>) =>
    apiFetch<{ updated: boolean }>('settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  disconnect: () =>
    apiFetch<{ disconnected: boolean }>('settings/disconnect', { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Legacy data migration (Settings → Data Migration)
// ---------------------------------------------------------------------------

export interface MigrationStatus {
  configured: boolean;            // is a seeUwell read connection set up at all
  crossAccountEnabled: boolean;   // may this tenant import from a DIFFERENT account
  agencyAdmin: boolean;           // caller is an agency admin (endpoint is agency-admin gated)
  mapped: boolean;                // does the (source) location map to a legacy org
  legacyOrgId: string | null;
  source: string | null;          // the source GHL location id, when cross-account
  counts: {
    legacyForms: number;
    legacySubmissions: number;
    legacyMessages: number;
    migratedForms: number;
    migratedSubmissions: number;
    migratedMessages: number;
  } | null;
}

export interface MigrationRunResult {
  legacyOrgId: string;
  source: string | null;
  forms?: { created: number; skipped: number };
  submissions?: { created: number; skipped: number; skippedNoForm: number };
  messages?: { created: number; skipped: number; skippedNoContact: number };
}

export const migration = {
  /** Pass a source GHL location id to preview a DIFFERENT account (gated server-side). */
  status: (source?: string) =>
    apiFetch<MigrationStatus>(`settings/migration${source ? `?source=${encodeURIComponent(source)}` : ''}`),
  run: (opts: { forms?: boolean; submissions?: boolean; messages?: boolean; sourceLocationId?: string } = {}) =>
    apiFetch<MigrationRunResult>('settings/migration/run', { method: 'POST', body: JSON.stringify(opts) }),
  /** Clear migrated rows. Pass a scope to clear selectively (forms imply their
   *  submissions, FK); omit all flags to clear everything. */
  clear: (scope?: { forms?: boolean; submissions?: boolean; messages?: boolean }) =>
    apiFetch<{ formsDeleted: number; submissionsDeleted: number; messagesDeleted: number; attachmentsDeleted?: number; formAttachmentsDeleted?: number }>('settings/migration/clear', {
      method: 'POST', body: JSON.stringify({ confirm: 'CLEAR', ...(scope ?? {}) }),
    }),
};
